import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Phone, Shield, Clock, Star, CheckCircle2, Award, MapPin, Car } from "lucide-react";
import heroImage from "@/assets/cities/san-antonio-hero.jpg";

const CANONICAL = "/chauffeur-service-san-antonio";
const SITE = "https://ricksblackcarservicesantonio.com";
const PHONE = "+1-210-960-4047";
const SQUARE_BOOK_URL = "https://ricksblackcarservice.square.site/";

const faqs = [
  {
    q: "How much does a chauffeur service cost in San Antonio?",
    a: "Rick's local San Antonio chauffeur service starts at $110 for trips within 15 miles of downtown. SAT Airport transfers are a flat $150, and Austin transfers (AUS Airport or downtown) are a flat $474. A $35 late-night surcharge applies between 9 PM and 5 AM.",
  },
  {
    q: "What's the difference between a chauffeur service and a rideshare?",
    a: "A chauffeur service is a pre-booked, fixed-price ride with a professional, uniformed driver in a luxury vehicle. You get the same driver from pickup to drop-off, no surge pricing, no shared rides, and door-to-door care — including help with luggage. Rideshare apps assign whoever is closest in whatever car they drive.",
  },
  {
    q: "What vehicle will my chauffeur arrive in?",
    a: "Every ride is in a 2025 Audi Q7 Black Optic — a full-size luxury SUV with seating for up to 5 adults, leather interior, climate control, complimentary water, and generous luggage space. We operate a single, immaculately maintained vehicle so the experience is consistent every trip.",
  },
  {
    q: "Do you serve the entire San Antonio area?",
    a: "Yes. We cover all of San Antonio including downtown, the Pearl, Alamo Heights, Stone Oak, the Medical Center, La Cantera, Hill Country Village, and JBSA (Lackland, Randolph, Fort Sam Houston, Camp Bullis). We also handle longer trips to Austin, AUS Airport, the Hill Country, and the Texas wine country.",
  },
  {
    q: "How far in advance should I book a chauffeur in San Antonio?",
    a: "For airport transfers and corporate trips we recommend 24–48 hours notice to lock in your preferred time. Same-day and last-minute rides are accommodated when available and include a $25 last-minute add-on. Weddings, multi-stop hourly bookings, and Austin transfers should be reserved as early as possible.",
  },
  {
    q: "Are you veteran-owned?",
    a: "Yes. Rick's Black Car Service is a proudly veteran-owned San Antonio chauffeur company. We have deep familiarity with JBSA installations and offer respectful, on-time service for military families, retirees, and BMT graduation visitors.",
  },
];

const ChauffeurServiceSanAntonio = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE}${CANONICAL}#localbusiness`,
    name: "Rick's Black Car Service — Chauffeur Service San Antonio",
    description:
      "Veteran-owned chauffeur service in San Antonio, TX. Flat-rate SAT airport transfers, Austin transfers, corporate, weddings, and hourly luxury rides in a 2025 Audi Q7 Black Optic.",
    url: `${SITE}${CANONICAL}`,
    telephone: PHONE,
    image: `${SITE}/og-image.jpg`,
    priceRange: "$$$",
    address: {
      "@type": "PostalAddress",
      addressLocality: "San Antonio",
      addressRegion: "TX",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 29.4241, longitude: -98.4936 },
    areaServed: [
      { "@type": "City", name: "San Antonio" },
      { "@type": "City", name: "Austin" },
      { "@type": "City", name: "Boerne" },
      { "@type": "City", name: "New Braunfels" },
      { "@type": "AdministrativeArea", name: "Bexar County" },
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "127" },
    serviceType: ["Chauffeur Service", "Black Car Service", "Airport Transfer", "Corporate Transportation"],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const pricing = [
    { route: "Local San Antonio (within 15 mi)", price: "$110", note: "Base point-to-point rate" },
    { route: "SAT Airport Transfer (within 15 mi)", price: "$150", note: "Flat rate, all-inclusive" },
    { route: "Austin / AUS Airport Transfer", price: "$474", note: "Flat rate door-to-door" },
    { route: "Hourly Chauffeur (multi-stop)", price: "From $110/hr", note: "2-hour minimum" },
  ];

  const reasons = [
    { icon: <Award className="w-6 h-6" />, title: "Veteran-Owned", desc: "Trusted by JBSA personnel, families, and retirees across San Antonio." },
    { icon: <Car className="w-6 h-6" />, title: "2025 Audi Q7 Black Optic", desc: "One immaculate luxury SUV. No bait-and-switch fleet — what you book is what arrives." },
    { icon: <Shield className="w-6 h-6" />, title: "Flat-Rate Pricing", desc: "No surge pricing, no surprises. The quote you see is the price you pay." },
    { icon: <Clock className="w-6 h-6" />, title: "On-Time Guarantee", desc: "Flight tracking, traffic-aware routing, and 15-minute curb arrival window." },
  ];

  return (
    <div className="min-h-screen bg-background pt-20">
      <SEO
        title="Chauffeur Service San Antonio | Veteran-Owned Black Car — Rick's"
        description="Top-rated chauffeur service in San Antonio, TX. Flat $150 SAT airport, $474 Austin, $110 local. Veteran-owned, 2025 Audi Q7. Book 24/7."
        canonicalPath={CANONICAL}
        keywords="chauffeur service san antonio, san antonio chauffeur, black car service san antonio, executive chauffeur san antonio, private chauffeur san antonio, luxury chauffeur san antonio"
        schema={[localBusinessSchema, faqSchema]}
      />
      <Navbar />

      <div className="container mx-auto px-4 pt-4">
        <Breadcrumb items={[{ name: "Chauffeur Service San Antonio", url: CANONICAL }]} />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Chauffeur service in San Antonio, Texas" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40" />
        </div>
        <div className="relative container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-medium tracking-widest uppercase mb-5">
              <Star className="w-3.5 h-3.5 fill-primary" /> Veteran-Owned · 5.0 ★ San Antonio
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-foreground leading-tight mb-4">
              Chauffeur Service in San Antonio
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-7 leading-relaxed">
              Professional, flat-rate chauffeur service for SAT airport transfers, corporate travel, weddings, and Hill Country trips — all in a 2025 Audi Q7 Black Optic, driven by the same trusted veteran chauffeur every time.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="text-base">
                <a href={SQUARE_BOOK_URL} target="_blank" rel="noopener noreferrer">Book Now</a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-base">
                <a href={`tel:${PHONE}`}><Phone className="w-4 h-4 mr-2" />(210) 960-4047</a>
              </Button>
              <Button asChild variant="ghost" size="lg" className="text-base">
                <Link to="/quick-quote">Get Instant Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">Why San Antonio Books Rick's</h2>
            <p className="text-muted-foreground">A single chauffeur, a single luxury SUV, and a single promise: punctual, private, and predictable pricing.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reasons.map((r) => (
              <Card key={r.title} className="border-border bg-card hover:border-primary/40 transition-colors">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">{r.icon}</div>
                  <h3 className="font-semibold text-foreground mb-1.5">{r.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-card/40">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">San Antonio Chauffeur Pricing</h2>
            <p className="text-muted-foreground">Transparent flat rates. No surge pricing, no per-mile guessing.</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {pricing.map((p) => (
              <Card key={p.route} className="border-border">
                <CardContent className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold text-foreground">{p.route}</div>
                    <div className="text-sm text-muted-foreground">{p.note}</div>
                  </div>
                  <div className="font-display text-2xl font-bold text-primary whitespace-nowrap">{p.price}</div>
                </CardContent>
              </Card>
            ))}
            <p className="text-xs text-muted-foreground text-center pt-2">
              Late-night surcharge of $35 applies between 9 PM and 5 AM. Last-minute add-on of $25 may apply for same-day bookings.
            </p>
          </div>
          <div className="text-center mt-8">
            <Button asChild size="lg">
              <a href={SQUARE_BOOK_URL} target="_blank" rel="noopener noreferrer">Book Your Chauffeur</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">Neighborhoods We Serve</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Our San Antonio chauffeur service covers every major neighborhood, business district, and military installation across Bexar County and the surrounding Hill Country.
              </p>
              <ul className="grid grid-cols-2 gap-2 text-sm">
                {["Downtown / Riverwalk","The Pearl","Alamo Heights","Stone Oak","Medical Center","La Cantera","Hill Country Village","Olmos Park","Terrell Hills","Shavano Park","JBSA-Lackland","JBSA-Randolph","Fort Sam Houston","Camp Bullis","Boerne","New Braunfels"].map((n) => (
                  <li key={n} className="flex items-center gap-2 text-foreground"><MapPin className="w-3.5 h-3.5 text-primary" />{n}</li>
                ))}
              </ul>
            </div>
            <Card className="border-border">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-display text-2xl font-bold text-foreground">What's Included</h3>
                {["Same dedicated chauffeur every trip","Door-to-door curbside service","Complimentary bottled water","Flight tracking on every airport run","Luggage handling and child-seat-free space for adults","Real-time text updates on arrival","No surge pricing, ever"].map((f) => (
                  <div key={f} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">{f}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-card/40">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">San Antonio Chauffeur FAQ</h2>
            <p className="text-muted-foreground">Quick answers to what San Antonio riders ask most.</p>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">Ready to Book Your San Antonio Chauffeur?</h2>
          <p className="text-lg text-muted-foreground mb-8">Reserve online in under 60 seconds, or call to speak with Rick directly.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg">
              <a href={SQUARE_BOOK_URL} target="_blank" rel="noopener noreferrer">Book Now</a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href={`tel:${PHONE}`}><Phone className="w-4 h-4 mr-2" />(210) 960-4047</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChauffeurServiceSanAntonio;